import React, { useMemo, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";
import { type Lam2RollEvent, type Lam2ShiftRollsRow } from "../../../api/lam2";
import { ROLL_COLORS, fmtRolls, fmtShare } from "./Lam2RollsChart";

/**
 * Roll quality on a clock: every roll a colored segment spanning the time it
 * took to make, at the moment the PLC says it finished.
 *
 * Times and cycle lengths are the machine's own, out of DB46's per-roll ring.
 * Grade is the soft part — A/B/F exist only as per-shift counters, so a roll
 * whose grade couldn't be pinned down is drawn grey, and one attributed from a
 * poll that caught several differing grades says so on hover.
 *
 * Rolls only exist for shifts recorded since roll-event capture was deployed.
 * With none in range this falls back to shift bands colored by the shift's
 * A/B/F share, which is all the older data supports.
 */

const HOUR = 3_600_000;
/** Longest a shift can run before we treat the next gap as downtime. */
const MAX_SHIFT_MS = 12 * HOUR;
const FALLBACK_SHIFT_MS = 8 * HOUR;
const TRACK_HEIGHT = 34;

type Band = {
  key: number;
  start: number;
  end: number;
  row: Lam2ShiftRollsRow;
  label: string;
};

/** One roll, as drawn. */
type Segment = {
  key: number;
  start: number;
  end: number;
  grade: "A" | "B" | "F" | null;
  exact: boolean;
  seconds: number | null;
};

const GRADE_COLOR: Record<"A" | "B" | "F", string> = {
  A: ROLL_COLORS.a,
  B: ROLL_COLORS.b,
  F: ROLL_COLORS.f,
};
/** A roll the counters couldn't account for — timed, but ungraded. */
const UNGRADED_COLOR = "#9e9e9e";

/** Fallback bar length when the PLC recorded no cycle time for a roll. */
const DEFAULT_ROLL_MS = 45_000;

const ms = (iso: string): number | null => {
  try {
    const t = parseISO(iso).getTime();
    return Number.isFinite(t) ? t : null;
  } catch {
    return null;
  }
};

const median = (xs: number[]) => {
  if (!xs.length) return FALLBACK_SHIFT_MS;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

/**
 * Shift length isn't in the payload, so take it from the gap to the next shift
 * and fall back to the typical length for the last one (and after downtime).
 */
const toBands = (rows: Lam2ShiftRollsRow[]): Band[] => {
  const withStart = rows
    .map((row) => ({ row, start: ms(row.shift_start) }))
    .filter(
      (x): x is { row: Lam2ShiftRollsRow; start: number } => x.start !== null,
    )
    .sort((a, b) => a.start - b.start);

  const gaps = withStart
    .map((x, i) => (i > 0 ? x.start - withStart[i - 1].start : 0))
    .filter((g) => g > 0 && g <= MAX_SHIFT_MS);
  const typical = median(gaps);

  return withStart.map(({ row, start }, i) => {
    const next = withStart[i + 1]?.start;
    const gap = next === undefined ? Infinity : next - start;
    const length = gap <= MAX_SHIFT_MS ? gap : typical;
    let label = row.shift_start;
    try {
      label = format(parseISO(row.shift_start), "MMM d");
    } catch {
      /* keep raw */
    }
    return { key: row.shift_id, start, end: start + length, row, label };
  });
};

/**
 * Rolls -> segments. Each one ends when the PLC says it finished and runs back
 * over its own cycle time, so the strip reads as the line's actual rhythm:
 * evenly spaced while running, gapped wherever the winder stopped.
 */
const toSegments = (events: Lam2RollEvent[]): Segment[] =>
  events
    .map((e) => {
      const end = ms(e.timestamp);
      if (end === null) return null;
      const seconds = e.duration_s && e.duration_s > 0 ? e.duration_s : null;
      return {
        key: e.roll_seq,
        start: end - (seconds !== null ? seconds * 1000 : DEFAULT_ROLL_MS),
        end,
        grade: e.grade,
        exact: e.grade_exact,
        seconds,
      };
    })
    .filter((s): s is Segment => s !== null)
    .sort((a, b) => a.start - b.start);

/** Tick steps to choose from, coarsening as the span grows. */
const TICK_STEPS = [1, 2, 3, 4, 6, 8, 12, 24, 48, 72, 168].map((h) => h * HOUR);
const MAX_TICKS = 10;

/**
 * Ticks scaled to the span: clock times over a couple of shifts, dates over a
 * couple of weeks. Anchored to local midnight so they land on round hours (a
 * DST boundary can shift one by an hour — not worth correcting for a label).
 */
const axisTicks = (from: number, to: number) => {
  const span = to - from;
  const step =
    TICK_STEPS.find((s) => span / s <= MAX_TICKS) ??
    TICK_STEPS[TICK_STEPS.length - 1];

  const anchor = new Date(from);
  anchor.setHours(0, 0, 0, 0);

  const ticks: Array<{ t: number; label: string }> = [];
  for (let t = anchor.getTime(); t <= to; t += step) {
    if (t < from) continue;
    const d = new Date(t);
    const midnight = d.getHours() === 0 && d.getMinutes() === 0;
    ticks.push({
      t,
      label:
        step >= 24 * HOUR || midnight ? format(d, "MMM d") : format(d, "HH:mm"),
    });
  }
  return ticks;
};

/** How far, in pixels, the cursor may be from a roll and still pick it. */
const HOVER_SNAP_PX = 8;

/**
 * The roll under the cursor, or the nearest one within a few pixels.
 *
 * A snap rather than an exact hit: across a full shift one roll is a sliver
 * well under a pixel wide, and a hover that needs pixel-perfect aim is one
 * nobody gets to use.
 */
const rollAt = (
  segments: Segment[],
  t: number,
  snapMs: number,
): Segment | null => {
  let best: Segment | null = null;
  let bestGap = Infinity;
  for (const s of segments) {
    const gap = t < s.start ? s.start - t : t > s.end ? t - s.end : 0;
    if (gap < bestGap) {
      best = s;
      bestGap = gap;
    }
  }
  return best && bestGap <= snapMs ? best : null;
};

const GradeSwatch: React.FC<{ color: string }> = ({ color }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      width: 8,
      height: 8,
      borderRadius: "2px",
      bgcolor: color,
      mr: 0.75,
    }}
  />
);

/** Time and grade of the roll under the cursor. */
const RollTooltip: React.FC<{ segment: Segment }> = ({ segment: s }) => (
  <Box sx={{ py: 0.25 }}>
    <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
      {format(s.end, "HH:mm:ss")}
    </Typography>
    <Typography variant="caption" sx={{ display: "block" }}>
      <GradeSwatch color={s.grade ? GRADE_COLOR[s.grade] : UNGRADED_COLOR} />
      {s.grade ? `${s.grade} roll` : "Grade not attributable"}
    </Typography>
    {s.seconds !== null && (
      <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
        Cycle {s.seconds.toFixed(1)}s
      </Typography>
    )}
    {s.grade && !s.exact && (
      <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
        Grade uncertain (several rolls in one poll)
      </Typography>
    )}
  </Box>
);

const BandTooltip: React.FC<{ band: Band }> = ({ band }) => {
  const { row, label } = band;
  const grades: Array<[string, number, string]> = [
    ["A", row.a_rolls, ROLL_COLORS.a],
    ["B", row.b_rolls, ROLL_COLORS.b],
    ["F", row.f_rolls, ROLL_COLORS.f],
  ];
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
        {label} · {row.shift_name}
        {row.is_partial ? " (in progress)" : ""}
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
        {format(band.start, "HH:mm")}–{format(band.end, "HH:mm")} ·{" "}
        {fmtRolls(row.total)} rolls
      </Typography>
      {grades.map(([k, n, c]) => (
        <Typography key={k} variant="caption" sx={{ display: "block" }}>
          <GradeSwatch color={c} />
          {k} {fmtRolls(n)} ({fmtShare(n, row.total)})
        </Typography>
      ))}
    </Box>
  );
};

type Props = {
  rows: Lam2ShiftRollsRow[];
  /** Per-roll timing, when the worker has recorded any for this window. */
  events?: Lam2RollEvent[];
  /**
   * Explicit clock window. Without one the span covers the shifts in `rows`,
   * which is what the per-shift views want; a "last hour" view has to say so,
   * since its bounds don't line up with any shift.
   */
  window?: { from: number; to: number } | null;
};

const Lam2RollTimeline: React.FC<Props> = ({
  rows,
  events = [],
  window = null,
}) => {
  const bands = useMemo(() => toBands(rows), [rows]);
  const segments = useMemo(() => toSegments(events), [events]);
  /** The cursor's time on the track, while it is over the per-roll strip. */
  const [hover, setHover] = useState<{
    t: number;
    roll: Segment | null;
  } | null>(null);

  const span = useMemo(() => {
    if (window && window.to > window.from) {
      return { ...window, ms: window.to - window.from };
    }
    if (!bands.length) return null;
    const from = bands[0].start;
    const to = bands[bands.length - 1].end;
    return to > from ? { from, to, ms: to - from } : null;
  }, [bands, window]);

  if (!span) {
    return (
      <Typography variant="body2" color="text.secondary">
        No shift roll counts recorded yet.
      </Typography>
    );
  }

  const pos = (t: number) => ((t - span.from) / span.ms) * 100;
  const perRoll = segments.length > 0;

  // A window that opens partway through a shift makes that shift's band a lie:
  // its A/B/F share covers the whole shift, not the slice on screen, so say
  // what's missing rather than color the track from the wrong denominator. A
  // window that starts on a shift boundary — the shift browser's — bands
  // honestly, and still needs the fallback when no per-roll timing was kept.
  const windowOpensMidShift =
    !!window && bands.some((b) => b.start < span.from);
  if (windowOpensMidShift && !perRoll) {
    return (
      <Typography variant="body2" color="text.secondary">
        No rolls recorded in this window.
      </Typography>
    );
  }

  return (
    <Box>
      {/* The track shows through wherever nothing was produced. */}
      <Box
        sx={{
          position: "relative",
          height: TRACK_HEIGHT,
          borderRadius: 1,
          bgcolor: "action.hover",
          overflow: "hidden",
        }}
      >
        {perRoll ? (
          <Tooltip
            // Follows the cursor, so it reads as "this point on the clock"
            // rather than pinning to the whole strip.
            followCursor
            open={!!hover}
            title={
              hover?.roll ? (
                <RollTooltip segment={hover.roll} />
              ) : hover ? (
                <Typography variant="caption" sx={{ display: "block" }}>
                  {format(hover.t, "HH:mm")} · no roll
                </Typography>
              ) : (
                ""
              )
            }
          >
            <Box
              component="svg"
              data-testid="roll-segments"
              width="100%"
              height={TRACK_HEIGHT}
              preserveAspectRatio="none"
              sx={{ display: "block", cursor: "crosshair" }}
              onMouseMove={(e: React.MouseEvent<SVGSVGElement>) => {
                const r = e.currentTarget.getBoundingClientRect();
                if (r.width <= 0) return;
                const frac = Math.min(
                  Math.max((e.clientX - r.left) / r.width, 0),
                  1,
                );
                const t = span.from + frac * span.ms;
                const snapMs = (HOVER_SNAP_PX / r.width) * span.ms;
                setHover({ t, roll: rollAt(segments, t, snapMs) });
              }}
              onMouseLeave={() => setHover(null)}
            >
              {/* Shift boundaries, so the rolls still read as shifts. Only the
                ones actually inside the window — a clock range can start and
                end mid-shift. */}
              {bands
                .slice(1)
                .filter((b) => b.start > span.from && b.start < span.to)
                .map((b) => (
                  <line
                    key={`div-${b.key}`}
                    x1={`${pos(b.start)}%`}
                    x2={`${pos(b.start)}%`}
                    y1={0}
                    y2={TRACK_HEIGHT}
                    stroke="rgba(0,0,0,0.35)"
                    strokeWidth={1}
                  />
                ))}
              {segments.map((s) => {
                const x = pos(s.start);
                // Sub-pixel rolls are normal across a full shift; keep a floor so
                // a lone F is never invisible.
                const w = Math.max(pos(s.end) - x, 0.08);
                return (
                  <rect
                    key={s.key}
                    data-testid="roll-segment"
                    data-grade={s.grade ?? "unknown"}
                    x={`${x}%`}
                    width={`${w}%`}
                    y={0}
                    height={TRACK_HEIGHT}
                    fill={s.grade ? GRADE_COLOR[s.grade] : UNGRADED_COLOR}
                  />
                );
              })}
              {/* Marks the roll the tooltip is describing. */}
              {hover?.roll && (
                <rect
                  x={`${pos(hover.roll.start)}%`}
                  width={`${Math.max(pos(hover.roll.end) - pos(hover.roll.start), 0.15)}%`}
                  y={0}
                  height={TRACK_HEIGHT}
                  fill="none"
                  stroke="rgba(0,0,0,0.85)"
                  strokeWidth={2}
                  pointerEvents="none"
                />
              )}
            </Box>
          </Tooltip>
        ) : (
          bands.map((band) => {
            const { row } = band;
            const grades: Array<[string, number, string]> = [
              ["a", row.a_rolls, ROLL_COLORS.a],
              ["b", row.b_rolls, ROLL_COLORS.b],
              ["f", row.f_rolls, ROLL_COLORS.f],
            ];
            return (
              <Tooltip key={band.key} title={<BandTooltip band={band} />} arrow>
                <Box
                  data-testid="roll-band"
                  data-shift={band.key}
                  // Geometry stays inline: one emotion class per unique width
                  // would be a class per shift, for no gain.
                  style={{
                    left: `${pos(band.start)}%`,
                    width: `${Math.max(pos(band.end) - pos(band.start), 0.4)}%`,
                    // The shift in progress is faded, as on the bar chart.
                    opacity: row.is_partial ? 0.55 : 1,
                  }}
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    display: "flex",
                    cursor: "default",
                    "&:hover": { filter: "brightness(1.08)" },
                  }}
                >
                  {row.total === 0 ? (
                    // The shift ran but produced nothing — worth seeing, and a
                    // zero-width split would render as an empty gap instead.
                    <Box
                      sx={{
                        flex: 1,
                        bgcolor: "action.disabledBackground",
                        border: (t) => `1px dashed ${t.palette.divider}`,
                      }}
                    />
                  ) : (
                    grades
                      .filter(([, n]) => n > 0)
                      .map(([k, n, c]) => (
                        <Box key={k} sx={{ flexGrow: n, bgcolor: c }} />
                      ))
                  )}
                </Box>
              </Tooltip>
            );
          })
        )}
      </Box>

      {/* Time axis */}
      <Box sx={{ position: "relative", height: 20, mt: 0.5 }}>
        {axisTicks(span.from, span.to).map(({ t, label }) => {
          const x = pos(t);
          // The marks stay exactly on their boundary. The two on the track's
          // edges would hang half a label outside the card, so those tuck
          // inward — the tick keeps the position, the text moves.
          const nudge =
            x <= 0.01
              ? "translateX(50%)"
              : x >= 99.99
                ? "translateX(-50%)"
                : undefined;
          return (
            <Box
              key={t}
              style={{ left: `${x}%` }}
              sx={{
                position: "absolute",
                transform: "translateX(-50%)",
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  width: "1px",
                  height: 4,
                  bgcolor: "divider",
                  mx: "auto",
                  mb: 0.25,
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: "block", transform: nudge }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Lam2RollTimeline;
